import React, { useEffect, useMemo, useState } from 'react';
import api from '../api/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tag, Crown, Users, Calendar, X } from '@phosphor-icons/react';
import { toast } from 'sonner';

const STATUS = {
  active: { label: 'Attiva', cls: 'bg-emerald-100 text-emerald-800' },
  completed: { label: 'Posti esauriti', cls: 'bg-sky-100 text-sky-800' },
  expired: { label: 'Scaduta', cls: 'bg-neutral-200 text-neutral-700' },
  closed: { label: 'Chiusa', cls: 'bg-neutral-200 text-neutral-700' },
};

const today = () => new Date().toISOString().slice(0, 10);
const inDays = (n) => new Date(Date.now() + n * 86400000).toISOString().slice(0, 10);

const ProPromotionsPage = () => {
  const [data, setData] = useState(null);
  const [services, setServices] = useState([]);
  const [form, setForm] = useState({
    service_id: '', discount_type: 'percent', discount_value: '',
    slots_total: '5', start_date: today(), end_date: inDays(30),
  });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [p, s] = await Promise.all([
      api.get('/api/professionals/me/promotions'),
      api.get('/api/professionals/me/services').catch(() => ({ data: [] })),
    ]);
    setData(p.data);
    const list = Array.isArray(s.data) ? s.data : (s.data?.items || []);
    setServices(list);
  };

  useEffect(() => { load().catch(() => toast.error('Errore caricamento promozioni')); }, []);

  const selectedService = useMemo(
    () => services.find((s) => s.service_id === form.service_id),
    [services, form.service_id],
  );

  const create = async () => {
    if (!form.service_id) { toast.error('Scegli la prestazione'); return; }
    if (!form.discount_value) { toast.error('Indica lo sconto'); return; }
    setBusy(true);
    try {
      await api.post('/api/professionals/me/promotions', {
        ...form,
        discount_value: Number(form.discount_value),
        slots_total: Number(form.slots_total),
      });
      toast.success('Promozione pubblicata');
      setForm({ ...form, service_id: '', discount_value: '' });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore creazione promozione');
    } finally { setBusy(false); }
  };

  const close = async (id) => {
    try {
      await api.post(`/api/professionals/me/promotions/${id}/close`);
      toast.success('Promozione chiusa');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore chiusura promozione');
    }
  };

  if (!data) {
    return <div className="p-8 text-foreground/60" data-testid="promotions-loading">Caricamento promozioni…</div>;
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 py-12 space-y-5" data-testid="pro-promotions-page">
        <div>
          <h1 className="font-heading font-semibold text-4xl mb-2">Promozioni</h1>
          <p className="text-foreground/70">
            Offri una prestazione a prezzo scontato per un numero limitato di clienti: la promozione compare
            ai clienti della tua zona che cercano quella prestazione.
          </p>
        </div>

        <Card className={`rounded-2xl p-4 flex items-start gap-3 ${data.is_premium ? 'bg-amber-50 border-amber-200' : 'bg-sky-50 border-sky-200'}`} data-testid="promotions-limit-card">
          <Crown size={22} weight="duotone" className={data.is_premium ? 'text-amber-600' : 'text-sky-700'} />
          <div className="text-sm">
            {data.is_premium ? (
              <p><strong>Premium attivo</strong>: promozioni illimitate su tutte le prestazioni che vuoi.</p>
            ) : (
              <>
                <p><strong>Profilo base</strong>: 1 promozione al mese su una sola prestazione
                  (questo mese: {data.created_this_month}/1 usata).</p>
                <p className="text-foreground/70 mt-1">
                  Con Premium le promozioni diventano illimitate e attivabili su tutte le prestazioni.
                </p>
              </>
            )}
          </div>
        </Card>

        <Card className="rounded-2xl p-6" data-testid="promotion-form">
          <p className="font-heading font-medium text-lg mb-4">Nuova promozione</p>
          {!data.can_create && (
            <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4" data-testid="promotion-limit-reached">
              Hai già pubblicato la promozione di questo mese. Passa a Premium per crearne quante vuoi.
            </p>
          )}
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <Label>Prestazione</Label>
              <Select value={form.service_id} onValueChange={(v) => setForm({ ...form, service_id: v })} disabled={!data.can_create}>
                <SelectTrigger data-testid="promotion-service"><SelectValue placeholder="Scegli la prestazione" /></SelectTrigger>
                <SelectContent>
                  {services.filter((s) => s.enabled !== false).map((s) => (
                    <SelectItem key={s.service_id} value={s.service_id}>
                      {s.name} — €{Number(s.price_eur ?? s.default_price_eur ?? 0).toFixed(2)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tipo di sconto</Label>
              <Select value={form.discount_type} onValueChange={(v) => setForm({ ...form, discount_type: v })} disabled={!data.can_create}>
                <SelectTrigger data-testid="promotion-discount-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="percent">Percentuale (%)</SelectItem>
                  <SelectItem value="fixed">Prezzo promozionale (€)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{form.discount_type === 'percent' ? 'Sconto (%)' : 'Prezzo promo (€)'}</Label>
              <Input type="number" min="1" value={form.discount_value} disabled={!data.can_create}
                onChange={(e) => setForm({ ...form, discount_value: e.target.value })}
                placeholder={form.discount_type === 'percent' ? 'Es. 20' : 'Es. 30'}
                data-testid="promotion-discount-value" />
            </div>
            <div>
              <Label>Per quanti clienti</Label>
              <Input type="number" min="1" value={form.slots_total} disabled={!data.can_create}
                onChange={(e) => setForm({ ...form, slots_total: e.target.value })} data-testid="promotion-slots" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Dal</Label>
                <Input type="date" value={form.start_date} disabled={!data.can_create}
                  onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="promotion-start" />
              </div>
              <div>
                <Label>Al</Label>
                <Input type="date" value={form.end_date} disabled={!data.can_create}
                  onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="promotion-end" />
              </div>
            </div>
          </div>

          {selectedService && form.discount_value && (
            <p className="text-sm text-foreground/70 mt-4" data-testid="promotion-preview">
              I clienti vedranno:{' '}
              <span className="line-through">€{Number(selectedService.price_eur ?? selectedService.default_price_eur ?? 0).toFixed(2)}</span>{' '}
              <strong className="text-primary">
                €{form.discount_type === 'percent'
                  ? (Number(selectedService.price_eur ?? selectedService.default_price_eur ?? 0) * (1 - Number(form.discount_value) / 100)).toFixed(2)
                  : Number(form.discount_value).toFixed(2)}
              </strong>{' '}
              per i primi {form.slots_total} clienti.
            </p>
          )}

          <Button onClick={create} disabled={busy || !data.can_create} className="rounded-full mt-5" data-testid="promotion-create-btn">
            <Tag size={16} className="mr-1.5" /> Pubblica promozione
          </Button>
        </Card>

        <div className="space-y-3">
          <p className="text-xs uppercase tracking-wider text-foreground/55">Le tue promozioni ({data.items.length})</p>

          {data.items.length > 0 && data.totals && (
            <Card className="rounded-2xl p-5 bg-primary/5 border-primary/20" data-testid="promotions-totals">
              <p className="text-sm font-medium mb-3">Risultati complessivi delle promozioni</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div>
                  <p className="font-heading font-bold text-2xl">{data.totals.requests}</p>
                  <p className="text-[11px] text-foreground/60 uppercase tracking-wide">Richieste</p>
                </div>
                <div>
                  <p className="font-heading font-bold text-2xl">{data.totals.accepted}</p>
                  <p className="text-[11px] text-foreground/60 uppercase tracking-wide">Accettate</p>
                </div>
                <div>
                  <p className="font-heading font-bold text-2xl">{data.totals.completed}</p>
                  <p className="text-[11px] text-foreground/60 uppercase tracking-wide">Completate</p>
                </div>
                <div>
                  <p className="font-heading font-bold text-2xl text-primary">€{Number(data.totals.revenue).toFixed(2)}</p>
                  <p className="text-[11px] text-foreground/60 uppercase tracking-wide">Incassi</p>
                </div>
              </div>
            </Card>
          )}
          {data.items.length === 0 ? (
            <Card className="rounded-2xl p-6 text-center bg-muted/20" data-testid="promotions-empty">
              <p className="text-foreground/70">Non hai ancora pubblicato promozioni.</p>
            </Card>
          ) : data.items.map((p) => (
            <Card key={p.promotion_id} className="rounded-2xl p-5" data-testid={`promotion-card-${p.promotion_id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <p className="font-heading font-medium">{p.service_name}</p>
                    <Badge className={STATUS[p.status]?.cls} data-testid={`promotion-status-${p.promotion_id}`}>
                      {STATUS[p.status]?.label || p.status}
                    </Badge>
                  </div>
                  <p className="text-sm">
                    <span className="line-through text-foreground/50">€{p.original_price.toFixed(2)}</span>{' '}
                    <strong className="text-primary text-lg">€{p.promo_price.toFixed(2)}</strong>{' '}
                    <span className="text-foreground/60 text-xs">
                      ({p.discount_type === 'percent' ? `-${p.discount_value}%` : 'prezzo fisso'})
                    </span>
                  </p>
                  <div className="flex flex-wrap gap-4 mt-2 text-xs text-foreground/65">
                    <span className="flex items-center gap-1"><Users size={14} /> {p.slots_used}/{p.slots_total} clienti · {p.slots_left} posti liberi</span>
                    <span className="flex items-center gap-1"><Calendar size={14} /> {new Date(p.start_date).toLocaleDateString('it-IT')} – {new Date(p.end_date).toLocaleDateString('it-IT')}</span>
                  </div>
                </div>
                {p.status === 'active' && (
                  <Button size="sm" variant="outline" className="rounded-full h-8" onClick={() => close(p.promotion_id)} data-testid={`promotion-close-${p.promotion_id}`}>
                    <X size={14} className="mr-1" /> Chiudi
                  </Button>
                )}
              </div>

              {p.stats && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 pt-3 border-t border-border/60" data-testid={`promotion-stats-${p.promotion_id}`}>
                  <div>
                    <p className="font-heading font-semibold text-lg">{p.stats.requests}</p>
                    <p className="text-[10px] text-foreground/60 uppercase tracking-wide">Richieste</p>
                  </div>
                  <div>
                    <p className="font-heading font-semibold text-lg">{p.stats.accepted}</p>
                    <p className="text-[10px] text-foreground/60 uppercase tracking-wide">Accettate</p>
                  </div>
                  <div>
                    <p className="font-heading font-semibold text-lg">{p.stats.completed}</p>
                    <p className="text-[10px] text-foreground/60 uppercase tracking-wide">Completate</p>
                  </div>
                  <div>
                    <p className="font-heading font-semibold text-lg text-primary">€{Number(p.stats.revenue_eur).toFixed(2)}</p>
                    <p className="text-[10px] text-foreground/60 uppercase tracking-wide">Incassi</p>
                  </div>
                  {p.stats.requests > 0 && (
                    <p className="col-span-2 sm:col-span-4 text-[11px] text-foreground/60">
                      Conversione richieste → prestazioni completate: <strong>{p.stats.conversion_rate}%</strong>
                      {p.stats.discount_given_eur > 0 && <> · sconto concesso ai clienti: €{Number(p.stats.discount_given_eur).toFixed(2)}</>}
                    </p>
                  )}
                </div>
              )}
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProPromotionsPage;
